<template>
  <div
    v-if="visible"
    class="rie-layer"
    role="dialog"
    aria-modal="true"
    aria-labelledby="rie-title"
    @keydown.esc="close"
  >
    <section ref="dialog" class="rie-dialog" tabindex="-1">
      <header class="rie-head">
        <h2 id="rie-title">{{ title }}</h2>
        <div>
          <button class="rie-close" type="button" aria-label="关闭图片编辑" :disabled="submitting" @click="close">×</button>
        </div>
      </header>

      <div class="rie-toolbar">
        <div class="rie-group" role="group" aria-label="图片编辑工具">
          <button type="button" :class="{ active: tool === 'brush' }" :aria-pressed="tool === 'brush'" :disabled="loading || submitting" @click="setTool('brush')">笔刷</button>
          <button type="button" :class="{ active: tool === 'rectangle' }" :aria-pressed="tool === 'rectangle'" :disabled="loading || submitting" @click="setTool('rectangle')">框选</button>
          <button type="button" :class="{ active: tool === 'eraser' }" :aria-pressed="tool === 'eraser'" :disabled="loading || submitting" @click="setTool('eraser')">橡皮</button>
          <button type="button" :class="{ active: tool === 'crop' }" :aria-pressed="tool === 'crop'" :disabled="loading || submitting" @click="setTool('crop')">裁切</button>
        </div>
        <label class="rie-input">
          <span>颜色</span>
          <input v-model="color" type="color" aria-label="标注颜色" :disabled="loading || submitting" @input="updateCursor" />
        </label>
        <label class="rie-input rie-size">
          <span>粗细</span>
          <input v-model.number="size" type="range" min="1" max="80" step="1" aria-label="标注粗细" :disabled="loading || submitting" @input="updateCursor" />
          <strong>{{ size }}</strong>
        </label>
        <div class="rie-group" role="group" aria-label="编辑历史">
          <button type="button" :disabled="history.length <= 1 || submitting" @click="undo">撤销</button>
          <button type="button" :disabled="loading || submitting" @click="reset">重置</button>
          <button v-if="tool === 'crop'" type="button" :disabled="!crop || submitting" @click="applyCrop">应用裁切</button>
        </div>
      </div>

      <div ref="stage" class="rie-stage">
        <div v-if="loading" class="rie-state" role="status" aria-live="polite"><i class="el-icon-loading"></i>正在加载图片</div>
        <div v-else-if="loadError" class="rie-state error" role="alert">{{ loadError }}</div>
        <div v-else ref="canvasWrap" class="rie-canvas-wrap">
          <div class="rie-canvas-stack" :style="canvasDisplayStyle">
            <canvas
              ref="canvas"
              aria-label="图片区域标记画布"
              @pointerdown="pointerDown"
              @pointermove="pointerMove"
              @pointerup="pointerUp"
              @pointercancel="pointerUp"
            ></canvas>
            <div v-if="tool === 'crop' && crop" class="rie-crop" :style="cropStyle"></div>
            <div v-if="tool === 'rectangle' && rectangle" class="rie-rectangle" :style="rectangleStyle"></div>
            <div v-if="submitting" class="rie-processing" role="status" aria-live="polite">
              <i class="el-icon-loading" aria-hidden="true"></i>
              <strong>AI 正在处理标记区域</strong>
            </div>
          </div>
        </div>
      </div>

      <section class="rie-prompt" aria-label="AI 编辑提示词">
        <div class="rie-prompt-head">
          <label for="rie-prompt-input">{{ promptLabel }}</label>
          <span>{{ prompt.length }}/1200</span>
        </div>
        <textarea
          id="rie-prompt-input"
          v-model="prompt"
          maxlength="1200"
          :placeholder="promptPlaceholder"
          :disabled="submitting || optimizingPrompt"
        ></textarea>
        <div class="rie-prompt-actions">
          <AiPromptOptimizer
            v-model="prompt"
            :disabled="loading || submitting"
            :max-length="1200"
            :context="optimizerContext"
            :image-urls="sourceUrl ? [sourceUrl] : []"
            :conversation-id="conversationId"
            @loading="optimizingPrompt = $event"
          />
          <button
            class="rie-submit"
            type="button"
            :disabled="!canSave"
            :aria-busy="String(submitting)"
            @click="save"
          >
            <i v-if="submitting" class="el-icon-loading" aria-hidden="true"></i>
            {{ submitting ? '处理中…' : saveText }}
          </button>
        </div>
        <p v-if="operationError || error" class="rie-error" role="alert">{{ operationError || error }}</p>
      </section>
    </section>
  </div>
</template>

<script>
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { downloadResource } from '@/services/api'

export default {
  name: 'ReusableImageEditor',
  components: { AiPromptOptimizer },
  props: {
    visible: { type: Boolean, default: false },
    sourceUrl: { type: String, default: '' },
    draft: { type: Object, default: null },
    submitting: { type: Boolean, default: false },
    error: { type: String, default: '' },
    defaultSize: { type: Number, default: 10 },
    defaultColor: { type: String, default: '#ff3b30' },
    initialPrompt: { type: String, default: '' },
    title: { type: String, default: '图片编辑' },
    saveText: { type: String, default: '保存并替换' },
    promptLabel: { type: String, default: '编辑提示词' },
    promptPlaceholder: { type: String, default: '说明要怎样修改标记区域' },
    optimizerContext: { type: String, default: '根据图片和用户标记区域，优化局部图片编辑提示词；保持用户原始意图，明确要修改的内容和必须保留的内容。' },
    markerGuide: { type: String, default: '' },
    executionGuard: { type: String, default: '' },
    conversationId: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      loadError: '',
      operationError: '',
      optimizingPrompt: false,
      prompt: '',
      tool: 'brush',
      color: this.defaultColor,
      size: this.defaultSize,
      drawing: false,
      pointerId: null,
      last: null,
      crop: null,
      rectangle: null,
      history: [],
      original: null,
      initialEdited: null,
      sourceRect: null,
      session: 0,
      canvasDisplayStyle: {},
      resizeObserver: null,
      fitFrame: null
    }
  },
  computed: {
    cropStyle() {
      if (!this.crop) return {}
      const left = Math.min(this.crop.x1, this.crop.x2)
      const top = Math.min(this.crop.y1, this.crop.y2)
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${Math.abs(this.crop.x2 - this.crop.x1)}px`,
        height: `${Math.abs(this.crop.y2 - this.crop.y1)}px`
      }
    },
    rectangleStyle() {
      if (!this.rectangle) return {}
      const canvas = this.canvas()
      const displayWidth = Number.parseFloat(this.canvasDisplayStyle.width) || (canvas ? canvas.width : 0)
      const scale = canvas && canvas.width ? displayWidth / canvas.width : 1
      return {
        left: `${Math.min(this.rectangle.rx1, this.rectangle.rx2)}px`,
        top: `${Math.min(this.rectangle.ry1, this.rectangle.ry2)}px`,
        width: `${Math.abs(this.rectangle.rx2 - this.rectangle.rx1)}px`,
        height: `${Math.abs(this.rectangle.ry2 - this.rectangle.ry1)}px`,
        borderColor: this.color,
        borderWidth: `${Math.max(1, this.size * scale)}px`
      }
    },
    canSave() {
      return !this.loading && !this.submitting && !this.optimizingPrompt && this.history.length > 1 && Boolean(this.prompt.trim())
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.load()
          this.$nextTick(() => {
            if (this.$refs.dialog) this.$refs.dialog.focus()
            this.bindResizeObserver()
            this.scheduleCanvasFit()
          })
        } else {
          this.unbindResizeObserver()
        }
      }
    },
    sourceUrl(value, oldValue) {
      if (this.visible && value !== oldValue) this.load()
    },
    draft(value, oldValue) {
      if (this.visible && value !== oldValue) this.load()
    },
    initialPrompt(value) {
      if (this.visible && !this.prompt.trim()) this.prompt = String(value || '')
    }
  },
  mounted() {
    window.addEventListener('resize', this.scheduleCanvasFit)
  },
  beforeDestroy() {
    this.session += 1
    window.removeEventListener('resize', this.scheduleCanvasFit)
    this.unbindResizeObserver()
    if (this.fitFrame) window.cancelAnimationFrame(this.fitFrame)
  },
  methods: {
    async load() {
      if (!this.sourceUrl) return
      const session = ++this.session
      Object.assign(this, {
        loading: true,
        loadError: '',
        operationError: '',
        optimizingPrompt: false,
        prompt: String(this.initialPrompt || ''),
        tool: 'brush',
        color: this.defaultColor,
        size: this.defaultSize,
        crop: null,
        rectangle: null,
        history: []
      })
      try {
        const sourceBlob = this.draft && this.draft.cleanBlob
          ? this.draft.cleanBlob
          : await downloadResource(this.sourceUrl, { timeoutMs: 60000 })
        const editedBlob = this.draft && this.draft.blob ? this.draft.blob : sourceBlob
        const loadImage = blob => new Promise((resolve, reject) => {
          const url = URL.createObjectURL(blob)
          const image = new Image()
          image.src = url
          image.onload = () => { URL.revokeObjectURL(url); resolve(image) }
          image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('图片加载失败')) }
        })
        const [image, editedImage] = await Promise.all([loadImage(sourceBlob), loadImage(editedBlob)])
        if (session !== this.session || !this.visible) return
        this.original = image
        this.initialEdited = editedImage
        this.loading = false
        this.$nextTick(() => {
          this.restoreLoadedDraft()
          this.bindResizeObserver()
          this.scheduleCanvasFit()
        })
      } catch (error) {
        if (session === this.session) {
          this.loading = false
          this.loadError = error.message || '图片加载失败'
        }
      }
    },
    canvas() {
      return this.$refs.canvas
    },
    ctx() {
      const canvas = this.canvas()
      return canvas && canvas.getContext('2d')
    },
    restoreOriginal() {
      const canvas = this.canvas()
      if (!canvas || !this.original) return
      canvas.width = this.original.naturalWidth
      canvas.height = this.original.naturalHeight
      this.sourceRect = { x: 0, y: 0, width: canvas.width, height: canvas.height }
      this.ctx().drawImage(this.original, 0, 0)
      this.history = []
      this.pushHistory()
      this.crop = null
      this.rectangle = null
      this.operationError = ''
      this.scheduleCanvasFit()
    },
    restoreLoadedDraft() {
      const canvas = this.canvas()
      if (!canvas || !this.original || !this.initialEdited) return
      canvas.width = this.initialEdited.naturalWidth
      canvas.height = this.initialEdited.naturalHeight
      this.sourceRect = { x: 0, y: 0, width: this.original.naturalWidth, height: this.original.naturalHeight }
      this.ctx().drawImage(this.initialEdited, 0, 0, canvas.width, canvas.height)
      this.history = []
      this.pushHistory()
      this.crop = null
      this.rectangle = null
      this.scheduleCanvasFit()
    },
    setTool(tool) {
      this.tool = tool
      if (tool !== 'crop') this.crop = null
      if (tool !== 'rectangle') this.rectangle = null
      this.updateCursor()
    },
    point(event) {
      const canvas = this.canvas()
      const rect = canvas.getBoundingClientRect()
      return {
        x: (event.clientX - rect.left) * canvas.width / rect.width,
        y: (event.clientY - rect.top) * canvas.height / rect.height,
        rx: event.clientX - rect.left,
        ry: event.clientY - rect.top
      }
    },
    cleanCanvas() {
      const canvas = this.canvas()
      if (!canvas || !this.original || !this.sourceRect) return null
      const clean = document.createElement('canvas')
      clean.width = canvas.width
      clean.height = canvas.height
      clean.getContext('2d').drawImage(
        this.original,
        this.sourceRect.x,
        this.sourceRect.y,
        this.sourceRect.width,
        this.sourceRect.height,
        0,
        0,
        clean.width,
        clean.height
      )
      return clean
    },
    pointerDown(event) {
      if ((event.pointerType === 'mouse' && event.button !== 0) || this.loading || this.submitting) return
      event.preventDefault()
      const canvas = this.canvas()
      const point = this.point(event)
      this.operationError = ''
      this.drawing = true
      this.pointerId = event.pointerId
      this.last = point
      if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId)
      if (this.tool === 'crop') {
        this.crop = { x1: point.rx, y1: point.ry, x2: point.rx, y2: point.ry }
        return
      }
      if (this.tool === 'rectangle') {
        this.rectangle = {
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y,
          rx1: point.rx,
          ry1: point.ry,
          rx2: point.rx,
          ry2: point.ry
        }
        return
      }
      this.segment(point, point)
    },
    pointerMove(event) {
      if (!this.drawing || event.pointerId !== this.pointerId) return
      event.preventDefault()
      const point = this.point(event)
      if (this.tool === 'crop') {
        this.crop.x2 = point.rx
        this.crop.y2 = point.ry
      } else if (this.tool === 'rectangle') {
        this.updateRectangle(point)
      } else {
        this.segment(this.last, point)
        this.last = point
      }
    },
    pointerUp(event) {
      if (!this.drawing || (event && event.pointerId !== this.pointerId)) return
      const canvas = this.canvas()
      const pointerId = this.pointerId
      const completedTool = this.tool
      const cancelled = Boolean(event && event.type === 'pointercancel')
      if (!cancelled && event && (completedTool === 'crop' || completedTool === 'rectangle')) {
        const point = this.point(event)
        if (completedTool === 'crop' && this.crop) {
          this.crop.x2 = point.rx
          this.crop.y2 = point.ry
        } else if (completedTool === 'rectangle') {
          this.updateRectangle(point)
        }
      }
      this.drawing = false
      this.pointerId = null
      this.last = null
      if (canvas && canvas.hasPointerCapture && canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId)
      if (cancelled && completedTool === 'crop') {
        this.crop = null
      } else if (completedTool === 'rectangle') {
        if (cancelled) {
          this.rectangle = null
          return
        }
        if (this.commitRectangle()) this.pushHistory()
        this.rectangle = null
      } else if (completedTool !== 'crop') {
        this.pushHistory()
      }
    },
    updateRectangle(point) {
      if (!this.rectangle) return
      this.rectangle.x2 = point.x
      this.rectangle.y2 = point.y
      this.rectangle.rx2 = point.rx
      this.rectangle.ry2 = point.ry
    },
    commitRectangle() {
      const context = this.ctx()
      if (!context || !this.rectangle) return false
      const left = Math.min(this.rectangle.x1, this.rectangle.x2)
      const top = Math.min(this.rectangle.y1, this.rectangle.y2)
      const width = Math.abs(this.rectangle.x2 - this.rectangle.x1)
      const height = Math.abs(this.rectangle.y2 - this.rectangle.y1)
      if (width < 2 || height < 2) {
        this.operationError = '框选区域太小，请拖动选择需要标注的区域'
        return false
      }
      context.save()
      context.globalCompositeOperation = 'source-over'
      context.strokeStyle = this.color
      context.lineWidth = this.size
      context.lineJoin = 'round'
      context.strokeRect(left, top, width, height)
      context.restore()
      return true
    },
    segment(from, to) {
      const context = this.ctx()
      if (!context) return
      context.save()
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.lineWidth = this.size
      context.globalCompositeOperation = this.tool === 'eraser' ? 'destination-out' : 'source-over'
      context.strokeStyle = this.color
      context.fillStyle = this.color
      context.beginPath()
      context.moveTo(from.x, from.y)
      context.lineTo(to.x, to.y)
      context.stroke()
      context.beginPath()
      context.arc(to.x, to.y, this.size / 2, 0, Math.PI * 2)
      context.fill()
      if (this.tool === 'eraser') {
        const clean = this.cleanCanvas()
        if (clean) {
          context.globalCompositeOperation = 'destination-over'
          context.drawImage(clean, 0, 0)
        }
      }
      context.restore()
    },
    pushHistory() {
      const canvas = this.canvas()
      if (!canvas || !this.sourceRect) return
      this.history.push({
        width: canvas.width,
        height: canvas.height,
        data: this.ctx().getImageData(0, 0, canvas.width, canvas.height),
        sourceRect: { ...this.sourceRect }
      })
      if (this.history.length > 20) this.history.shift()
    },
    undo() {
      if (this.history.length <= 1 || this.submitting) return
      this.history.pop()
      const item = this.history[this.history.length - 1]
      const canvas = this.canvas()
      canvas.width = item.width
      canvas.height = item.height
      this.sourceRect = { ...item.sourceRect }
      this.ctx().putImageData(item.data, 0, 0)
      this.crop = null
      this.rectangle = null
      this.operationError = ''
      this.scheduleCanvasFit()
    },
    reset() {
      if (this.submitting) return
      this.restoreOriginal()
      this.tool = 'brush'
      this.color = this.defaultColor
      this.size = this.defaultSize
      this.scheduleCanvasFit()
    },
    applyCrop() {
      if (!this.crop || this.submitting) return
      const canvas = this.canvas()
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      const x = Math.max(0, Math.round(Math.min(this.crop.x1, this.crop.x2) * scaleX))
      const y = Math.max(0, Math.round(Math.min(this.crop.y1, this.crop.y2) * scaleY))
      const width = Math.min(canvas.width - x, Math.round(Math.abs(this.crop.x2 - this.crop.x1) * scaleX))
      const height = Math.min(canvas.height - y, Math.round(Math.abs(this.crop.y2 - this.crop.y1) * scaleY))
      if (width < 4 || height < 4) {
        this.operationError = '裁切区域太小，请重新选择'
        return
      }
      const data = this.ctx().getImageData(x, y, width, height)
      const previous = this.sourceRect
      this.sourceRect = {
        x: previous.x + x * previous.width / canvas.width,
        y: previous.y + y * previous.height / canvas.height,
        width: width * previous.width / canvas.width,
        height: height * previous.height / canvas.height
      }
      canvas.width = width
      canvas.height = height
      this.ctx().putImageData(data, 0, 0)
      this.crop = null
      this.rectangle = null
      this.tool = 'brush'
      this.pushHistory()
      this.scheduleCanvasFit()
    },
    bindResizeObserver() {
      this.unbindResizeObserver()
      if (typeof ResizeObserver === 'undefined' || !this.$refs.stage) return
      this.resizeObserver = new ResizeObserver(() => this.scheduleCanvasFit())
      this.resizeObserver.observe(this.$refs.stage)
    },
    unbindResizeObserver() {
      if (this.resizeObserver) this.resizeObserver.disconnect()
      this.resizeObserver = null
    },
    scheduleCanvasFit() {
      if (this.fitFrame) window.cancelAnimationFrame(this.fitFrame)
      this.fitFrame = window.requestAnimationFrame(() => {
        this.fitFrame = null
        this.fitCanvas()
      })
    },
    fitCanvas() {
      const canvas = this.canvas()
      const wrap = this.$refs.canvasWrap
      if (!canvas || !wrap || !canvas.width || !canvas.height || !wrap.clientWidth || !wrap.clientHeight) return
      const scale = Math.min(wrap.clientWidth / canvas.width, wrap.clientHeight / canvas.height, 1)
      this.canvasDisplayStyle = {
        width: `${Math.max(1, Math.floor(canvas.width * scale))}px`,
        height: `${Math.max(1, Math.floor(canvas.height * scale))}px`
      }
      this.$nextTick(this.updateCursor)
    },
    updateCursor() {
      const canvas = this.canvas()
      if (!canvas) return
      if (this.tool === 'crop' || this.tool === 'rectangle') {
        canvas.style.cursor = 'crosshair'
        return
      }
      const rect = canvas.getBoundingClientRect()
      const scale = canvas.width > 0 && rect.width > 0 ? rect.width / canvas.width : 1
      const radius = Math.max(2, Math.min(120, this.size * scale / 2))
      const cursorSize = Math.ceil(radius * 2 + 6)
      const center = cursorSize / 2
      const stroke = this.tool === 'eraser' ? '#222' : this.color
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${cursorSize}' height='${cursorSize}'><circle cx='${center}' cy='${center}' r='${radius}' fill='none' stroke='${stroke}' stroke-width='2'/><circle cx='${center}' cy='${center}' r='${radius}' fill='none' stroke='white' stroke-width='1'/></svg>`
      canvas.style.cursor = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${center} ${center}, crosshair`
    },
    executionPrompt() {
      const markerGuide = this.markerGuide.trim() || '【可视标记编辑说明】输入图片上的彩色笔迹或矩形框只用于告诉你“这里需要修改”，这些标记本身不是画面内容，也不代表删除、透明或留白。请先识别彩色标记覆盖、圈出或框选的目标，再按照用户要求修改该位置。最终图片必须彻底去掉彩色标记并自然还原其下方画面，禁止留下白块、色块、圆形、空洞、边框或任何画笔痕迹。未标记区域保持原图的主体、构图、比例、位置、材质、颜色和光线不变。'
      return [this.prompt.trim(), markerGuide, this.executionGuard.trim()].filter(Boolean).join('\n\n')
    },
    async save() {
      if (!this.canSave) return
      const canvas = this.canvas()
      const clean = this.cleanCanvas()
      if (!canvas || !clean) return
      this.operationError = ''
      const editedData = this.ctx().getImageData(0, 0, canvas.width, canvas.height).data
      const cleanData = clean.getContext('2d').getImageData(0, 0, clean.width, clean.height).data
      let changedPixels = 0
      for (let index = 0; index < editedData.length; index += 4) {
        const changed = Math.abs(editedData[index] - cleanData[index])
          + Math.abs(editedData[index + 1] - cleanData[index + 1])
          + Math.abs(editedData[index + 2] - cleanData[index + 2])
          + Math.abs(editedData[index + 3] - cleanData[index + 3]) > 16
        if (changed) changedPixels += 1
      }
      if (!changedPixels) {
        this.operationError = '请先使用笔刷或框选标记需要修改的区域'
        return
      }
      const toBlob = target => new Promise(resolve => target.toBlob(resolve, 'image/png'))
      const [blob, cleanBlob] = await Promise.all([toBlob(canvas), toBlob(clean)])
      if (!blob || !cleanBlob) {
        this.operationError = '编辑数据生成失败，请重新标记后再试'
        return
      }
      this.$emit('save', {
        sourceUrl: this.sourceUrl,
        blob,
        cleanBlob,
        width: canvas.width,
        height: canvas.height,
        color: this.color,
        size: this.size,
        rawPrompt: this.prompt.trim(),
        prompt: this.executionPrompt()
      })
    },
    close() {
      if (!this.submitting) this.$emit('close')
    }
  }
}
</script>

<style scoped>
.rie-layer{position:fixed;z-index:3400;inset:0;display:grid;place-items:center;box-sizing:border-box;padding:18px;background:rgba(8,13,24,.72)}
.rie-dialog{width:min(1280px,calc(100vw - 36px));height:min(960px,calc(100dvh - 36px));display:grid;grid-template-rows:auto auto minmax(260px,1fr) auto;overflow:hidden;border:1px solid #8eb5f5;border-radius:18px;background:#fff;box-shadow:0 28px 90px rgba(8,13,24,.35)}
.rie-head{min-height:72px;display:flex;align-items:center;justify-content:space-between;gap:12px;box-sizing:border-box;border-bottom:1px solid #dce3ed;padding:10px 20px}.rie-head h2{margin:0;color:#161b22;font-size:24px}.rie-head>div{display:flex;align-items:center;gap:8px}.rie-head .rie-close{width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;border:0;border-radius:10px;padding:0;background:transparent;color:#161b22;font-size:30px;font-weight:400;cursor:pointer}.rie-head .rie-close:hover:not(:disabled){background:#f1f4f8}.rie-head .rie-close:disabled{opacity:.45;cursor:not-allowed}.rie-head button:focus-visible,.rie-toolbar button:focus-visible,.rie-input input:focus-visible,.rie-prompt textarea:focus-visible,.rie-submit:focus-visible{outline:3px solid rgba(37,99,235,.32);outline-offset:2px}
.rie-toolbar{display:flex;flex-wrap:wrap;align-items:center;gap:14px;border-bottom:1px solid #dce3ed;padding:10px 20px;background:#f7f9fc}.rie-group{display:flex;align-items:center;gap:4px;border:1px solid #d6deea;border-radius:12px;padding:4px;background:#fff}.rie-group button{min-height:44px;border:0;border-radius:9px;padding:0 18px;background:transparent;color:#161b22;font-size:14px;font-weight:800;cursor:pointer}.rie-group button:hover:not(:disabled){background:#edf2f8}.rie-group button.active{background:#2f7df4;color:#fff}.rie-group button:disabled{opacity:.42;cursor:not-allowed}.rie-input{display:flex;align-items:center;gap:10px;color:#6b7481;font-size:14px;font-weight:800}.rie-input input[type=color]{width:50px;height:44px;border:1px solid #d6deea;border-radius:10px;padding:5px;background:#fff;cursor:pointer}.rie-input input:disabled{opacity:.45;cursor:not-allowed}.rie-size input{width:230px;accent-color:#1478f2}.rie-size strong{min-width:26px;color:#596373;font-variant-numeric:tabular-nums}
.rie-stage{min-width:0;min-height:0;display:grid;place-items:center;overflow:hidden;padding:24px;background:#f0f2f5}.rie-canvas-wrap{width:100%;height:100%;min-width:0;min-height:0;display:grid;place-items:center;overflow:hidden}.rie-canvas-stack{max-width:100%;max-height:100%;position:relative;overflow:visible;line-height:0}.rie-canvas-stack canvas{width:100%;height:100%;display:block;border-radius:8px;background:#fff;box-shadow:0 8px 22px rgba(0,0,0,.14);touch-action:none}.rie-crop{position:absolute;border:2px dashed #2f7df4;background:rgba(47,125,244,.14);pointer-events:none}.rie-rectangle{position:absolute;box-sizing:border-box;border-style:solid;pointer-events:none}.rie-state{display:flex;align-items:center;gap:8px;color:#566170;font-size:14px}.rie-state.error,.rie-error{color:#b83232}.rie-processing{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:10px;border-radius:8px;background:rgba(8,13,24,.66);color:#fff}.rie-processing i{font-size:28px}.rie-processing strong{font-size:14px}
.rie-prompt{box-sizing:border-box;border-top:1px solid #dce3ed;padding:14px 20px 16px;background:#fff}.rie-prompt-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px}.rie-prompt-head label{color:#252b34;font-size:14px;font-weight:800}.rie-prompt-head span{color:#7c8795;font-size:12px;font-variant-numeric:tabular-nums}.rie-prompt textarea{width:100%;min-height:72px;max-height:130px;resize:vertical;box-sizing:border-box;border:1px solid #cfd8e5;border-radius:10px;padding:10px 12px;color:#1f2937;font-family:inherit;font-size:14px;line-height:1.55}.rie-prompt textarea:disabled{background:#f3f5f8;color:#7b8490;cursor:not-allowed}.rie-prompt-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px;margin-top:10px}.rie-prompt-actions::v-deep .ai-prompt-optimizer{width:auto;flex:0 1 auto;margin:0}.rie-prompt::v-deep .ai-prompt-optimizer button{min-height:44px}.rie-submit{min-width:132px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid #216ee8;border-radius:9px;padding:0 18px;background:#2f7df4;color:#fff;font-family:inherit;font-size:14px;font-weight:800;cursor:pointer;box-shadow:0 5px 14px rgba(47,125,244,.2);transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease,transform .18s ease}.rie-submit:hover:not(:disabled){border-color:#155fcf;background:#246fe2;box-shadow:0 7px 18px rgba(47,125,244,.28);transform:translateY(-1px)}.rie-submit:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;transform:none}.rie-error{margin:8px 0 0;font-size:12px;line-height:1.5}
@media(max-width:760px){.rie-layer{padding:0}.rie-dialog{width:100%;height:100dvh;grid-template-rows:auto auto minmax(220px,1fr) auto;border:0;border-radius:0}.rie-head{min-height:62px;padding:8px 12px}.rie-head h2{font-size:20px}.rie-toolbar{gap:8px;padding:8px 10px}.rie-group button{min-height:44px;padding:0 12px}.rie-size{width:100%}.rie-size input{min-width:0;flex:1}.rie-stage{padding:10px}.rie-prompt{padding:10px 12px calc(12px + env(safe-area-inset-bottom))}.rie-prompt textarea{min-height:64px;max-height:92px}}
@media(max-width:480px){.rie-toolbar{max-height:150px;overflow-y:auto}.rie-input input[type=color]{width:44px}.rie-prompt-head label{font-size:13px}.rie-prompt-actions{align-items:stretch;flex-direction:column}.rie-prompt-actions::v-deep .ai-prompt-optimizer{width:100%;justify-content:flex-end}.rie-submit{width:100%}}
@media(prefers-reduced-motion:reduce){.rie-layer{scroll-behavior:auto}.rie-submit{transition:none}.rie-submit:hover:not(:disabled){transform:none}}
</style>
